import { TAB_CONFIG } from '@/lib/navigation/navConfig';
import type { TabRouteName } from '@/lib/navigation/navConfig';
import { triggerSelectionHaptic } from '@/lib/platform/haptics';
import { useTabSwipeGuardStore } from '@/lib/useTabSwipeGuardStore';
import { router } from 'expo-router';
import * as React from 'react';
import { Platform, View, useWindowDimensions } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';

const TAB_ROUTE_ORDER = Object.keys(TAB_CONFIG) as TabRouteName[];
const MAP_DISTANCE = 72;
const MAP_VELOCITY = 420;
const MAP_AXIS_RATIO = 1.45;
const MAP_EDGE_WIDTH = 16;
const BOTTOM_GESTURE_GUARD = 112;

function shouldAcceptSwipe({
  horizontal,
  vertical,
  velocityX,
  distance,
  velocity,
  axisRatio,
}: {
  horizontal: number;
  vertical: number;
  velocityX: number;
  distance: number;
  velocity: number;
  axisRatio: number;
}) {
  return (
    horizontal >= distance &&
    Math.abs(velocityX) >= velocity &&
    horizontal / Math.max(vertical, 1) >= axisRatio
  );
}

export function TabSwipeNavigationLayer({
  activeRoute,
  enabled,
  children,
}: {
  activeRoute: TabRouteName | null;
  enabled: boolean;
  children: React.ReactNode;
}) {
  const { height } = useWindowDimensions();
  const mode = useTabSwipeGuardStore((state) => state.mode);
  const isBlocked = useTabSwipeGuardStore((state) => state.isBlocked);

  const navigateBySwipe = React.useCallback(
    (direction: 'next' | 'previous') => {
      if (!activeRoute) {
        return;
      }

      const currentIndex = TAB_ROUTE_ORDER.indexOf(activeRoute);
      const nextIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
      const nextRoute = TAB_ROUTE_ORDER[nextIndex];

      if (!nextRoute) {
        return;
      }

      void triggerSelectionHaptic();
      router.navigate(`/(tabs)/${nextRoute}` as never);
    },
    [activeRoute]
  );

  const edgeGesture = React.useCallback(
    (direction: 'next' | 'previous') =>
      Gesture.Pan()
        .enabled(enabled && Platform.OS !== 'web' && mode === 'map-strict' && !isBlocked)
        .activeOffsetX(direction === 'next' ? [-999, -12] : [12, 999])
        .failOffsetY([-42, 42])
        .runOnJS(true)
        .onEnd((event) => {
          if (!activeRoute || mode !== 'map-strict' || isBlocked) {
            return;
          }

          if (event.absoluteY > height - BOTTOM_GESTURE_GUARD) {
            return;
          }

          if (
            !shouldAcceptSwipe({
              horizontal: Math.abs(event.translationX),
              vertical: Math.abs(event.translationY),
              velocityX: event.velocityX,
              distance: MAP_DISTANCE,
              velocity: MAP_VELOCITY,
              axisRatio: MAP_AXIS_RATIO,
            })
          ) {
            return;
          }

          navigateBySwipe(direction);
        }),
    [activeRoute, enabled, height, isBlocked, mode, navigateBySwipe]
  );

  const leftEdgeGesture = React.useMemo(() => edgeGesture('previous'), [edgeGesture]);
  const rightEdgeGesture = React.useMemo(() => edgeGesture('next'), [edgeGesture]);

  const shouldShowMapEdges = mode === 'map-strict' && enabled && Platform.OS !== 'web';

  return (
    <View className="flex-1">
      {children}
      {shouldShowMapEdges ? (
        <>
          <GestureDetector gesture={leftEdgeGesture}>
            <View
              pointerEvents="box-only"
              style={{
                position: 'absolute',
                bottom: BOTTOM_GESTURE_GUARD,
                left: 0,
                top: 0,
                width: MAP_EDGE_WIDTH,
              }}
            />
          </GestureDetector>
          <GestureDetector gesture={rightEdgeGesture}>
            <View
              pointerEvents="box-only"
              style={{
                position: 'absolute',
                bottom: BOTTOM_GESTURE_GUARD,
                right: 0,
                top: 0,
                width: MAP_EDGE_WIDTH,
              }}
            />
          </GestureDetector>
        </>
      ) : null}
    </View>
  );
}
